import { useEffect, useMemo } from 'react';

import { useFlags } from '@mint-lib/routing-context';
import { useNavigate } from '@mint/routing';
import { useAtom } from 'jotai';

import {
  DEFAULT_LIMIT,
  ITEMS_PER_PAGE,
} from '../../store/atoms/TablePagination/constants.js';
import { tablePaginationStore } from '../../store/atoms/TablePagination/tablePaginationStore.js';
import { TablePaginationType } from '../../store/atoms/TablePagination/tablePaginationStore.types.js';
import useConditionalSearch from '../useConditionalSearch/useConditionalSearch.js';

const useTablePagination = (initialPagination?: TablePaginationType) => {
  const navigate = useNavigate();
  const ff = useFlags('@mint/arm', ['paginationImprovementsC']);

  const searchParams = useConditionalSearch();

  const [pagination, setPagination] = useAtom(tablePaginationStore);

  const setCurrentOffset = (currentPage: number) => {
    const offset = currentPage * pagination.limit;

    navigate({
      // @ts-expect-error TS2322 TODO: use routeApi navigate
      search: (prev) => ({ ...prev, offset }),
    });
  };

  const setCurrentPage = (currentPage: number) => {
    setPagination((state) => {
      state.currentPage = currentPage;
    });

    setCurrentOffset(currentPage);
  };

  const setTotalItems = (totalItems: number) => {
    setPagination((state) => {
      state.totalItems = totalItems;
    });
  };

  const setLimit = (limit: number) => {
    navigate({
      // @ts-expect-error TS2322 TODO: use routeApi navigate
      search: (prev) => {
        return { ...prev, limit, offset: 0 };
      },
    }).then(() => {
      setCurrentPage(0);
      setPagination((state) => {
        state.limit = limit;
      });
    });
  };

  const resetPagination = (defaultPagination: TablePaginationType) => {
    setPagination(defaultPagination);
  };

  useEffect(() => {
    if (initialPagination) {
      // initial current page for table pagination component
      // if user provide offset in route search params
      const currentPage =
        searchParams.offset && searchParams.limit
          ? Math.floor(searchParams.offset / searchParams.limit)
          : initialPagination.currentPage;

      const limit =
        searchParams.limit && searchParams.limit > 0
          ? searchParams.limit
          : initialPagination.limit;

      const offset =
        searchParams.offset && searchParams.offset > 0
          ? searchParams.offset
          : initialPagination.offset;

      resetPagination({
        ...initialPagination,
        limit,
        offset,
        currentPage,
      });
    }
  }, [JSON.stringify(initialPagination)]);

  const isPaginationHide = ff.paginationImprovementsC.enabled
    ? pagination.totalItems < DEFAULT_LIMIT
    : pagination.totalItems <= (searchParams.limit ?? DEFAULT_LIMIT);

  const rowsPerPageOptions = useMemo(() => {
    if (ff.paginationImprovementsC.enabled) {
      const firstGreater = ITEMS_PER_PAGE.find(
        (count) => count > pagination.totalItems,
      );
      const validOptions = ITEMS_PER_PAGE.filter(
        (count) => count <= pagination.totalItems,
      );
      return [...validOptions, ...(firstGreater ? [firstGreater] : [])];
    }
    return ITEMS_PER_PAGE;
  }, [pagination.totalItems, ff]);

  return {
    limit: searchParams.limit || DEFAULT_LIMIT,
    offset: searchParams.offset,
    setLimit,
    currentPage: pagination.currentPage,
    setCurrentPage,
    totalItems: pagination.totalItems,
    setTotalItems,
    setPagination,
    isPaginationHide,
    rowsPerPageOptions,
  };
};

export default useTablePagination;
